import React, { useState } from 'react';
import {
    Button, Typography, TextField, IconButton, InputAdornment, Alert, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Link as MuiLink
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import '../../component-files/Modals/Modal.css';
import figLogo from '../../assets/fig_CROP.png';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const API_BASE = 'https://figazuresql.swancreate.com';
const API_USERS = `${API_BASE}/api/swa/UserMst`; // list & PUT by apiid
const MAILGUN_SEND = 'https://mailgun.swancreate.com/send';
const MS_LOGIN_REDIRECT = process.env.REACT_APP_MS_LOGIN_URL || '/api/auth/microsoft';

// Hosted FIG logo for email rendering (3x3" container in the email)
const FIG_LOGO_URL = 'https://figk-12.com/static/media/fig_CROP.cb0d7cf4314c63ba05ee.png';

const Login = () => {
    const { login } = useAuth();
    const navigate = useNavigate();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // forgot-password dialog
    const [fpOpen, setFpOpen] = useState(false);
    const [fpEmail, setFpEmail] = useState('');
    const [fpBusy, setFpBusy] = useState(false);
    const [fpMsg, setFpMsg] = useState('');
    const [fpErr, setFpErr] = useState('');

    const handleLogin = async () => {
        setError(''); setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/swa/usermst/lookup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: username, password })
            });

            if (res.status === 200) {
                const user = await res.json();
                login({
                    UserId: user.UserId,
                    apiid: user.apiid,
                    username: `${user.FirstName} ${user.LastName}`,
                    email: user.Email,
                    jobTitle: user.JobTitle,
                    profileImage: user.ProfileImage || figLogo,
                    SchoolDistrictId: user.SchoolDistrictId,
                    schoolDistrictName: user.DistrictName,
                    RoleId: user.RoleId,
                    IsActive: user.IsActive,
                    cashierPin: user.CashierPin,
                    isAllowCashierLogin: user.IsAllowCashierLogin,
                });
                navigate('/');
            } else if (res.status === 401) {
                setError('Invalid username or password.');
            } else if (res.status === 404) {
                setError('User not found.');
            } else {
                const err = await res.json().catch(() => ({}));
                setError(err.error || 'Login failed, please try again.');
            }
        } catch (e) {
            console.error(e);
            setError('Network error, please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = e => { if (e.key === 'Enter') handleLogin(); };
    const handleMicrosoftLogin = () => { window.location.href = MS_LOGIN_REDIRECT; };

    const openForgot = () => {
        setFpEmail(username || '');
        setFpErr('');
        setFpMsg('');
        setFpOpen(true);
    };

    const handleSendReset = async () => {
        setFpErr(''); setFpMsg('');
        const email = fpEmail.trim();
        if (!email) return setFpErr('Please enter your email.');

        setFpBusy(true);
        try {
            // 1) find user by email (case-insensitive)
            const { data: users } = await axios.get(API_USERS);
            const user = users.find(u => (u.Email || '').toLowerCase() === email.toLowerCase());

            if (!user) {
                setFpErr('We could not find that email in our system.');
                setFpBusy(false);
                return;
            }

            // 2) set a one-time reset token into PasswordResetId (PUT by apiid)
            const token = uuidv4();
            await axios.put(`${API_USERS}/${user.apiid}`, { PasswordResetId: token });

            // 3) compose reset link (query-string; route is /auth/passwordreset)
            const resetLink = `${window.location.origin}/auth/ResetPassword` +
                `?u=${encodeURIComponent(user.apiid)}` +
                `&t=${encodeURIComponent(token)}`;

            // 4) modern email with instructions + 3x3" logo container
            const html = `
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" border="0" style="background:#f5f7fa;padding:24px 0;">
          <tr>
            <td align="center">
              <!-- CARD -->
              <table role="presentation" width="600" cellspacing="0" cellpadding="0" border="0" style="max-width:600px;width:100%;background:#ffffff;border:1px solid #e6e9ef;border-radius:16px;overflow:hidden;">
                <tr>
                  <td align="center" style="padding:24px 24px 8px 24px">
                    <!-- 3 x 3 inch logo container -->
                    <table role="presentation" width="288" height="288" cellspacing="0" cellpadding="0" border="0"
                           style="width:3in;height:3in;max-width:288px;max-height:288px;border:1px solid #e6e9ef;border-radius:12px;background:#fff;overflow:hidden;">
                      <tr>
                        <td align="center" valign="middle" style="padding:12px">
                          <img src="${FIG_LOGO_URL}" width="230" height="230" alt="FIG K12"
                               style="display:block;border:0;outline:none;text-decoration:none;width:230px;height:230px;max-width:90%;max-height:90%;" />
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                <tr>
                  <td style="padding:8px 24px 0 24px; font-family:system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif; color:#0f172a;">
                    <h1 style="margin:16px 0 8px 0;font-size:22px;line-height:28px;font-weight:700;color:#0f172a;">Reset your FIG password</h1>
                    <p style="margin:0 0 12px 0;font-size:14px;line-height:20px;color:#334155;">
                      You’re receiving this email because a password reset was requested for your account.
                      Follow the steps below to finish:
                    </p>
                    <ol style="margin:0 0 16px 20px;padding:0;font-size:14px;line-height:20px;color:#334155;">
                      <li>Click the button below to open the secure reset page.</li>
                      <li>Enter your new password and confirm it.</li>
                      <li>Select <strong>Save new password</strong> to finish.</li>
                    </ol>

                    <!-- BUTTON -->
                    <div style="text-align:center;margin:24px 0;">
                      <a href="${resetLink}"
                         style="background:#84BD00;color:#000;text-decoration:none;display:inline-block;
                                padding:12px 20px;border-radius:8px;font-weight:600;font-size:14px;">
                        Set new password
                      </a>
                    </div>

                    <!-- FALLBACK LINK -->
                    <p style="margin:0 0 12px 0;font-size:12px;line-height:18px;color:#475569;">
                      If the button doesn’t work, copy and paste this link into your browser:
                    </p>
                    <p style="margin:0 0 16px 0;word-break:break-all;font-size:12px;line-height:18px;color:#2563eb;">
                      <a href="${resetLink}" style="color:#2563eb;text-decoration:underline;">${resetLink}</a>
                    </p>

                    <!-- INFO / EXPIRY -->
                    <p style="margin:0 0 8px 0;font-size:12px;line-height:18px;color:#64748b;">
                      This link is valid for the next <strong>60 minutes</strong>. If you didn’t request a reset, you can safely ignore this email.
                    </p>

                    <!-- FOOTER -->
                    <hr style="border:none;border-top:1px solid #e6e9ef;margin:20px 0;">
                    <p style="margin:0 0 4px 0;font-size:11px;line-height:16px;color:#94a3b8;">
                      Need help? Reply to this email or contact your district administrator.
                    </p>
                    <p style="margin:0 0 16px 0;font-size:11px;line-height:16px;color:#94a3b8;">
                      © ${new Date().getFullYear()} FIG K12
                    </p>
                  </td>
                </tr>
              </table>
              <!-- /CARD -->
            </td>
          </tr>
        </table>
      `;

            const text = `Reset your FIG password

You’re receiving this email because a password reset was requested for your account.

Steps:
1) Open the secure reset page:
   ${resetLink}
2) Enter your new password and confirm it.
3) Select "Save new password" to finish.

This link is valid for the next 60 minutes. If you didn’t request this, you can safely ignore this email.`;

            // 5) send the email (Mailgun JSON endpoint)
            await fetch(MAILGUN_SEND, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', accept: '*/*' },
                body: JSON.stringify({ to: user.Email, subject: 'FIG K12 — Password Reset', html, text })
            });

            setFpMsg('A reset link has been sent to your email.');
        } catch (e) {
            console.error(e);
            setFpErr('Unable to send reset link. Please try again.');
        } finally {
            setFpBusy(false);
        }
    };

    return (
        <div style={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            width: '100vw',
            backgroundColor: '#fff',
            padding: '16px',
            textAlign: 'center',
            color: '#000'
        }}>
            {/* lightly-watermarked background */}
            <div style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${figLogo})`,
                backgroundSize: '480px 480px',
                backgroundRepeat: 'repeat',
                opacity: 0.25,
                zIndex: 0,
            }} />

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '32px',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                border: '1px solid #ccc',
                backgroundColor: 'rgba(255,255,255,0.9)',
                maxWidth: '400px',
                width: '100%',
                position: 'relative',
                zIndex: 1,
                color: '#000'
            }}>
                <img
                    src={figLogo}
                    alt="FIG Logo"
                    style={{ width: '100%', maxWidth: '100px', marginBottom: '16px' }}
                />

                <Typography variant="h4" color="#000" gutterBottom>
                    FIG K12 Login
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* Email */}
                <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    onKeyPress={handleKeyPress}
                    sx={{
                        mb: 2,
                        bgcolor: '#fff',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#cbd5e1' },          // light border
                            '&:hover fieldset': { borderColor: '#94a3b8' },    // slightly darker on hover
                            '&.Mui-focused fieldset': { borderColor: '#0f172a' }, // dark on focus
                        },
                        '& .MuiInputBase-input': {
                            color: '#0f172a',
                            '::placeholder': { color: '#475569', opacity: 1 },
                        },
                        '& .MuiInputAdornment-root .MuiIconButton-root': { color: '#0f172a' },
                        '& .MuiInputLabel-root': { color: '#0f172a', '&.Mui-focused': { color: '#0f172a' } },
                        '& input:-webkit-autofill': {
                            WebkitTextFillColor: '#0f172a',
                            boxShadow: '0 0 0px 1000px #fff inset',
                            caretColor: '#0f172a',
                            transition: 'background-color 5000s ease-in-out 0s',
                        },
                    }}
                />

                {/* Password */}
                <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    sx={{
                        mb: 1,
                        bgcolor: '#fff',
                        borderRadius: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            '& fieldset': { borderColor: '#cbd5e1' },
                            '&:hover fieldset': { borderColor: '#94a3b8' },
                            '&.Mui-focused fieldset': { borderColor: '#0f172a' },
                        },
                        '& .MuiInputBase-input': {
                            color: '#0f172a',
                            '::placeholder': { color: '#475569', opacity: 1 },
                        },
                        '& .MuiInputAdornment-root .MuiIconButton-root': { color: '#0f172a' },
                        '& .MuiInputLabel-root': { color: '#0f172a', '&.Mui-focused': { color: '#0f172a' } },
                        '& input:-webkit-autofill': {
                            WebkitTextFillColor: '#0f172a',
                            boxShadow: '0 0 0px 1000px #fff inset',
                            caretColor: '#0f172a',
                            transition: 'background-color 5000s ease-in-out 0s',
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(p => !p)} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />

                {/* Forgot password link */}
                <div style={{ width: '100%', textAlign: 'right', marginBottom: 16 }}>
                    <MuiLink component="button" variant="body2" onClick={openForgot} sx={{ color: '#0f172a' }}>
                        Forgot password?
                    </MuiLink>
                </div>

                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    onClick={handleLogin}
                    sx={{
                        bgcolor: '#84BD00',
                        mb: 2,
                        borderRadius: '8px',
                        ':hover': { bgcolor: '#C8DA30' },
                        color: '#000'
                    }}
                >
                    {loading ? 'Signing in…' : 'Login'}
                </Button>

                <Divider flexItem sx={{ mb: 2, color: '#0f172a' }}>or</Divider>

                <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => { window.location.href = MS_LOGIN_REDIRECT; }}
                    startIcon={
                        <img
                            src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                            alt="Microsoft logo"
                            style={{ width: 18 }}
                        />
                    }
                    sx={{ borderRadius: '8px', textTransform: 'none', color: '#0f172a', borderColor: '#0f172a' }}
                >
                    Sign in with Microsoft
                </Button>
            </div>

            {/* Forgot Password dialog */}
            <Dialog
                open={fpOpen}
                onClose={() => setFpOpen(false)}
                PaperProps={{
                    sx: {
                        bgcolor: '#fff',            // light dialog surface
                        color: '#0f172a'
                    }
                }}
            >
                <DialogTitle sx={{ color: '#0f172a' }}>Reset your password</DialogTitle>
                <DialogContent>
                    {fpMsg ? (
                        <Alert severity="success" sx={{ mt: 1 }}>{fpMsg}</Alert>
                    ) : (
                        <>
                            {fpErr && <Alert severity="error" sx={{ mb: 1 }}>{fpErr}</Alert>}
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Email"
                                type="email"
                                fullWidth
                                variant="outlined"
                                value={fpEmail}
                                onChange={e => setFpEmail(e.target.value)}
                                sx={{
                                    bgcolor: '#fff',
                                    borderRadius: 1,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '& fieldset': { borderColor: '#cbd5e1' },          // lighter border
                                        '&:hover fieldset': { borderColor: '#94a3b8' },
                                        '&.Mui-focused fieldset': { borderColor: '#0f172a' },
                                    },
                                    '& .MuiInputBase-input': {
                                        color: '#0f172a',
                                        '::placeholder': { color: '#475569', opacity: 1 },
                                    },
                                    '& .MuiInputLabel-root': { color: '#0f172a', '&.Mui-focused': { color: '#0f172a' } },
                                }}
                            />
                            <Typography variant="body2" sx={{ mt: 1, color: '#0f172a' }}>
                                We’ll email you a secure link to choose a new password.
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFpOpen(false)} disabled={fpBusy}>Close</Button>
                    {!fpMsg && (
                        <Button
                            onClick={handleSendReset}
                            variant="contained"
                            disabled={fpBusy}
                            sx={{
                                color: '#fff',
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.6)' } // keep readable when disabled
                            }}
                        >
                            {fpBusy ? <CircularProgress size={20} color="inherit" /> : 'Send link'}
                        </Button>

                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Login;
